import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { smartDate } from "@/lib/date";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClockRotateLeft,
  faFloppyDisk,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import {
  BlockThread,
  InlineThread,
  getBasicExtensions,
} from "@nnr/tiptap-extensions";
import {
  CollabHistoryVersion,
  watchPreviewContent,
} from "@tiptap-pro/extension-collaboration-history";
import { TiptapCollabProvider } from "@hocuspocus/provider";
import { CodeBlock } from "@tiptap/extension-code-block";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import { UnifiedButton } from "@/components/tiptap-ui/unified-button";
import { useEffect, useState } from "react";
import { Markdown } from "tiptap-markdown";
import { useAuthContext } from "../../context/auth-context";
import {
  AuthenticatedImageNodeWithView,
  LibraryImageNodeWithView,
  SectionTitleNodeWithView,
  YouTubeVideoNodeWithView,
} from "./nodes";

type VersionHistoryPanelProps = {
  editor: Editor | null;
  provider: TiptapCollabProvider | null;
  versions: CollabHistoryVersion[];
};

export const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  editor,
  provider,
  versions,
}) => {
  const { authDetails } = useAuthContext();
  const [versionName, setVersionName] = useState("");
  const [open, setOpen] = useState(false);
  const [previewingVersion, setPreviewingVersion] =
    useState<CollabHistoryVersion | null>(null);

  const previewEditor = useEditor({
    editable: false,
    immediatelyRender: false,
    extensions: [
      ...getBasicExtensions({ history: false }),
      CodeBlock,
      YouTubeVideoNodeWithView,
      SectionTitleNodeWithView,
      AuthenticatedImageNodeWithView,
      LibraryImageNodeWithView,
      Markdown.configure({
        transformCopiedText: true,
        transformPastedText: true,
      }),
      InlineThread,
      BlockThread,
    ],
  });

  useEffect(() => {
    if (!provider) {
      return;
    }
    const unbind = watchPreviewContent(provider, (content) => {
      previewEditor?.commands.setContent(content);
    });
    return () => {
      unbind();
    };
  }, [provider, previewEditor]);

  const sortedVersions = [...versions].sort((a, b) => b.version - a.version);

  function handleSaveVersion() {
    const trimmed = versionName.trim();
    const defaultName = `${authDetails?.name ?? ""} v${versions.length}`;
    editor?.commands.saveVersion(trimmed || defaultName);
    setVersionName("");
  }

  function handlePreview(version: CollabHistoryVersion) {
    setPreviewingVersion(version);
    provider?.previewVersion(version.version);
  }

  function handleRestore(version: CollabHistoryVersion) {
    const label = version.name || `version ${version.version}`;
    editor?.commands.revertToVersion(
      version.version,
      `Restored from ${label}`,
      `Before restoring ${label}`,
    );
    setPreviewingVersion(null);
  }

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <UnifiedButton aria-label="Version History" tooltip="Version History">
            <FontAwesomeIcon icon={faClockRotateLeft} />
          </UnifiedButton>
        </SheetTrigger>
        <SheetContent className="flex flex-col gap-4">
          <SheetHeader>
            <SheetTitle>Version History</SheetTitle>
          </SheetHeader>

          <div className="flex flex-row gap-2">
            <Input
              placeholder="Name this version (optional)"
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveVersion();
                }
              }}
            />
            <Button
              variant="outline"
              size="icon"
              aria-label="Save Version"
              title="Save Version"
              onClick={handleSaveVersion}
            >
              <FontAwesomeIcon icon={faFloppyDisk} />
            </Button>
          </div>

          <ScrollArea className="min-h-0 flex-1">
            <div className="flex flex-col gap-2">
              {sortedVersions.length === 0 && (
                <div className="text-muted-foreground text-sm">
                  No versions saved yet.
                </div>
              )}
              {sortedVersions.map((version) => (
                <div
                  key={version.version}
                  className="flex flex-row items-center justify-between gap-2 rounded-md border px-3 py-2"
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {version.name || `Version ${version.version}`}
                    </span>
                    <span className="text-muted-foreground text-xs">
                      {smartDate(new Date(version.date).toISOString())}
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePreview(version)}
                  >
                    Preview
                  </Button>
                </div>
              ))}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      <Dialog
        open={!!previewingVersion}
        onOpenChange={(isOpen) => !isOpen && setPreviewingVersion(null)}
      >
        <DialogContent className="flex h-[80vh] max-w-3xl flex-col gap-4">
          <DialogHeader>
            <DialogTitle>
              {previewingVersion?.name ||
                `Version ${previewingVersion?.version}`}
              {previewingVersion &&
                ` — ${smartDate(new Date(previewingVersion.date).toISOString())}`}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="min-h-0 flex-1 rounded-md border p-4">
            <EditorContent className="markdown" editor={previewEditor} />
          </ScrollArea>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPreviewingVersion(null)}
            >
              Close
            </Button>
            <Button
              onClick={() =>
                previewingVersion && handleRestore(previewingVersion)
              }
            >
              Restore this version
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
